import dayjs from 'dayjs';
import type { TaskListFilters } from '@/features/tasks/types/taskFilters';
import type { Task } from '@/types/task';

function matchesDateFilter(task: Task, filters: TaskListFilters) {
  const dateFilter = filters.date;
  const created = dayjs(task.createdAt).startOf('day');

  if (dateFilter === 'custom') {
    if (!filters.createdFrom && !filters.createdTo) return true;
    if (filters.createdFrom && created.isBefore(dayjs(filters.createdFrom).startOf('day'))) {
      return false;
    }
    if (filters.createdTo && created.isAfter(dayjs(filters.createdTo).startOf('day'))) {
      return false;
    }
    return true;
  }

  if (dateFilter === 'all') return true;

  const today = dayjs().startOf('day');

  switch (dateFilter) {
    case 'today':
      return created.isSame(today, 'day');
    case 'this_week':
      return created.isSame(today, 'week');
    case 'this_month':
      return created.isSame(today, 'month');
    default:
      return true;
  }
}

function getTaskAssigneeIds(task: Task): string[] {
  if (task.assignees?.length) {
    return task.assignees.map((item) => item.assigneeId);
  }
  return task.assigneeId ? [task.assigneeId] : [];
}

function myInvolvementIds(userId: string, employeeRef?: string): string[] {
  return employeeRef ? [userId, employeeRef] : [userId];
}

function isAssignedToUser(task: Task, userId: string, employeeRef?: string) {
  const assigneeIds = getTaskAssigneeIds(task);
  const myIds = new Set(myInvolvementIds(userId, employeeRef));
  return assigneeIds.some((id) => myIds.has(id));
}

function isCreatorOf(task: Task, userId: string, employeeRef?: string) {
  if (task.creatorId === userId) return true;
  return Boolean(employeeRef && task.creatorId === employeeRef);
}

/** I created the task and at least one assignee is not me (A→B / A→A+B). */
function hasAssigneeOtherThanMe(task: Task, userId: string, employeeRef?: string) {
  if (!isCreatorOf(task, userId, employeeRef)) return false;
  const myIds = new Set(myInvolvementIds(userId, employeeRef));
  return getTaskAssigneeIds(task).some((id) => !myIds.has(id));
}

function isUserInvolvedTask(task: Task, userId: string, employeeRef?: string) {
  return isAssignedToUser(task, userId, employeeRef) || isCreatorOf(task, userId, employeeRef);
}

/**
 * Same buckets as api-admin list sort:
 * 0 = Assigned To me (others assigned to me)
 * 1 = Self assigned
 * 2 = Assigned By me (I assigned to others)
 * 3 = Other
 */
export function assignmentSortRank(
  task: Task,
  userId: string,
  employeeRef?: string,
): number {
  const assignee = isAssignedToUser(task, userId, employeeRef);
  const creator = isCreatorOf(task, userId, employeeRef);
  if (assignee && !creator) return 0;
  if (assignee && creator) return 1;
  if (creator && !assignee) return 2;
  return 3;
}

export function compareTasksByAssignmentOrder(
  a: Task,
  b: Task,
  userId: string,
  employeeRef?: string,
): number {
  const rankA = assignmentSortRank(a, userId, employeeRef);
  const rankB = assignmentSortRank(b, userId, employeeRef);
  if (rankA !== rankB) return rankA - rankB;
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

function matchesAssignmentFilter(
  task: Task,
  assignments: TaskListFilters['assignment'],
  userId: string,
  employeeRef?: string,
) {
  if (assignments.length === 0) return true;
  return assignments.some((assignment) => {
    switch (assignment) {
      case 'assigned_to_me':
        return isAssignedToUser(task, userId, employeeRef) && !isCreatorOf(task, userId, employeeRef);
      case 'assigned_by_me':
        return hasAssigneeOtherThanMe(task, userId, employeeRef);
      case 'self_assigned':
        return isAssignedToUser(task, userId, employeeRef) && isCreatorOf(task, userId, employeeRef);
      default:
        return false;
    }
  });
}

function matchesSearch(task: Task, search: string) {
  const term = search.trim().toLowerCase();
  if (!term) return true;

  const description = task.description?.replace(/<[^>]+>/g, ' ').toLowerCase() ?? '';
  const meeting = task.meeting?.toLowerCase() ?? '';
  const header = task.header?.toLowerCase() ?? '';
  return (
    task.title.toLowerCase().includes(term) ||
    description.includes(term) ||
    meeting.includes(term) ||
    header.includes(term)
  );
}

function matchesDueDateFilter(task: Task, filters: TaskListFilters) {
  if (filters.dueDate === 'all') return true;

  if (!task.dueDate) return false;

  const due = dayjs(task.dueDate).startOf('day');
  const today = dayjs().startOf('day');

  if (filters.dueDate === 'today') {
    return due.isSame(today, 'day');
  }

  if (filters.dueDate === 'overdue') {
    return (
      due.isBefore(today, 'day') &&
      task.status !== 'COMPLETED' &&
      task.status !== 'CANCELLED'
    );
  }

  return true;
}

function matchesTask(
  task: Task,
  filters: TaskListFilters,
  userId: string,
  employeeRef?: string,
) {
  if (!isUserInvolvedTask(task, userId, employeeRef)) return false;
  if (!matchesSearch(task, filters.search)) return false;
  if (filters.assigneeId && !getTaskAssigneeIds(task).includes(filters.assigneeId)) return false;
  if (!matchesAssignmentFilter(task, filters.assignment, userId, employeeRef)) return false;
  if (filters.status.length > 0 && !filters.status.includes(task.status)) return false;
  if (filters.criticality === 'critical' && !task.isCritical) return false;
  if (filters.criticality === 'non_critical' && task.isCritical) return false;
  if (filters.tag.length > 0 && (!task.tag || !filters.tag.includes(task.tag))) return false;
  if (!matchesDateFilter(task, filters)) return false;
  if (!matchesDueDateFilter(task, filters)) return false;
  return true;
}

export function applyTaskFilters(
  tasks: Task[],
  filters: TaskListFilters,
  userId: string,
  employeeRef?: string,
): Task[] {
  if (!userId) return [];

  const userTasks = tasks.filter((task) => isUserInvolvedTask(task, userId, employeeRef));
  const matchingIds = new Set<string>();

  for (const task of userTasks) {
    if (!matchesTask(task, filters, userId, employeeRef)) continue;

    matchingIds.add(task.id);
    let current = task.parentId;
    while (current) {
      matchingIds.add(current);
      current = tasks.find((item) => item.id === current)?.parentId ?? null;
    }
  }

  return userTasks.filter((task) => matchingIds.has(task.id));
}
